import { generatePath } from 'react-router-dom'

import { NAV_ITEMS, AP_ITEMS, AR_ITEMS, CONTRACT_ITEMS } from '@components/Navigation/menuItems'
import { filterNavItems } from '@components/Navigation/utils'
import type { NavFilterUser } from '@components/Navigation/utils'

import { ContactParty } from '@models/ApproveitAccounting'
import routes from '@src/Routes/routes'

/**
 * Compute the route a user should land on when:
 *   - they just logged in, or
 *   - they hit a perm-gated route they don't have access to.
 *
 * Walks the same nav-item lists the sidebar renders, picks the first
 * one the user passes filterNavItems for, and returns its `to`. The
 * Dashboard entry is intentionally skipped — if the caller is the
 * ProtectedRoute fallback fired because Dashboard itself was gated,
 * returning `/dashboard` here would loop. We want the user's first
 * REAL work surface.
 *
 * Plan-locked entries are NOT candidates (filterNavItems' default): the
 * sidebar shows them locked, but landing someone on a paywall is not a
 * work surface. The Teams shell's landing (getTeamsLandingRoute) picks
 * from the same resolved list.
 *
 * The AI Assistant is only ever a fallback: it sits early in NAV_ITEMS and
 * would otherwise win over genuine work surfaces. When no work surface
 * passes, land on `/assistant` if the backend resolved it as usable,
 * otherwise on the personal settings route — auth-only on both shells (web
 * redirects /settings → /profile; the Teams routers render SettingsPage),
 * so a zero-permission user gets a real page instead of a redirect loop.
 *
 * Pure function. Callers should pass the freshest user object they
 * have; we don't read context here.
 */
export function getLandingRoute(user: NavFilterUser | undefined, showCustomers = true): string {
  if (!user) return routes.private.settings
  const candidates = [
    ...filterNavItems(NAV_ITEMS, user),
    // Mirror LeftNav/BurgerMenu: the vendor/customer contacts entries are
    // hidden by the showCustomers team flag, so apply the same per-route
    // restriction here — otherwise login/redirect could land a user on a
    // contacts route the sidebar intentionally hides.
    ...filterNavItems(AP_ITEMS, user, {
      [generatePath(routes.private.contacts, { contactParty: ContactParty.vendor })]: showCustomers,
    }),
    ...filterNavItems(AR_ITEMS, user, {
      [generatePath(routes.private.contacts, { contactParty: ContactParty.customer })]: showCustomers,
    }),
    ...filterNavItems(CONTRACT_ITEMS, user),
  ]
  const firstReal = candidates.find(
    (item) => item.to && item.to !== routes.private.dashboard && item.to !== routes.private.assistant,
  )
  if (firstReal?.to) return firstReal.to
  // NEVER `/dashboard` here — it is request.view-gated, so a zero-permission
  // user would bounce back through this function forever.
  const assistantAvailable = candidates.some((item) => item.to === routes.private.assistant)
  return assistantAvailable ? routes.private.assistant : routes.private.settings
}
